const STRIPE_BUSINESS_NAME_MAX_LENGTH = 100;
const ACRONYM_MAX_LETTER_LENGTH = 4;

const STOP_WORDS: ReadonlySet<string> = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "in",
  "nor",
  "of",
  "on",
  "or",
  "per",
  "the",
  "to",
  "vs",
  "via",
]);

const SUFFIX_MAP: ReadonlyMap<string, string> = new Map([
  ["llc", "LLC"],
  ["llp", "LLP"],
  ["inc", "Inc"],
  ["incorporated", "Incorporated"],
  ["corp", "Corp"],
  ["corporation", "Corporation"],
  ["ltd", "Ltd"],
  ["limited", "Limited"],
  ["co", "Co"],
]);

function sanitizeForBusinessName(input: string): string {
  return (
    input
      // Linear time on any input — the inner alternation never matches the
      // same character twice because [^<] excludes `<`.
      // eslint-disable-next-line security/detect-unsafe-regex
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/<[^>]*>/g, "")
      .replace(/javascript:/gi, "")
      .replace(/on\w+\s*=/gi, "")
  );
}

function lettersOnly(token: string): string {
  return token.replace(/\P{L}/gu, "");
}

function isAllUpperLetters(letters: string): boolean {
  return letters.length > 0 && letters === letters.toUpperCase();
}

function isIntentionallyMixedCase(token: string): boolean {
  const letters = lettersOnly(token);
  if (letters.length === 0) return false;
  const hasUpper = letters !== letters.toLowerCase();
  const hasLower = letters !== letters.toUpperCase();
  if (!hasUpper || !hasLower) return false;
  const firstUpperRestLower =
    letters[0] === letters[0].toUpperCase() &&
    letters.slice(1) === letters.slice(1).toLowerCase();
  return !firstUpperRestLower;
}

function leadingCap(token: string): string {
  const lower = token.toLowerCase();
  const idx = lower.search(/\p{L}/u);
  if (idx === -1) return lower;
  return lower.slice(0, idx) + lower[idx].toUpperCase() + lower.slice(idx + 1);
}

function applySuffixCorrection(token: string): string {
  const letters = lettersOnly(token);
  if (letters.length === 0) return token;
  const canonical = SUFFIX_MAP.get(letters.toLowerCase());
  if (!canonical) return token;
  const leadingPunct = token.match(/^\P{L}*/u)?.[0] ?? "";
  const trailingPunct = token.match(/\P{L}*$/u)?.[0] ?? "";
  return leadingPunct + canonical + trailingPunct;
}

/**
 * Normalize a merchant-typed business name: trims whitespace, strips XSS
 * vectors, smart-title-cases obviously-unintentional casing, and forces
 * canonical casing on common business suffixes (LLC, Inc, Corp, etc.).
 *
 * Total (cannot throw) and idempotent.
 */
export function normalizeBusinessName(name: string | undefined | null): string {
  if (!name || typeof name !== "string") return "";

  let value = sanitizeForBusinessName(name).replace(/\s+/g, " ").trim();
  if (value.length > STRIPE_BUSINESS_NAME_MAX_LENGTH) {
    value = value.slice(0, STRIPE_BUSINESS_NAME_MAX_LENGTH).trim();
  }
  if (value.length === 0) return "";

  const originalTokens = value.split(" ");
  const allLetters = lettersOnly(value);
  const wholeInputAllUpper = isAllUpperLetters(allLetters);

  const anyIntentionallyMixed = originalTokens.some(isIntentionallyMixedCase);
  const titleCaseFires = !anyIntentionallyMixed;

  const acronymPreservedIndices = new Set<number>();
  let processed: string[];
  if (titleCaseFires) {
    processed = originalTokens.map((token, idx) => {
      const letters = lettersOnly(token);
      const wasAllUpper = isAllUpperLetters(letters);

      if (
        !wholeInputAllUpper &&
        wasAllUpper &&
        letters.length > 0 &&
        letters.length <= ACRONYM_MAX_LETTER_LENGTH
      ) {
        acronymPreservedIndices.add(idx);
        return token;
      }

      let capped = leadingCap(token);
      const lettersLower = letters.toLowerCase();
      if (
        STOP_WORDS.has(lettersLower) &&
        idx !== 0 &&
        idx !== originalTokens.length - 1
      ) {
        capped = capped.toLowerCase();
      }
      return capped;
    });
  } else {
    processed = [...originalTokens];
  }

  processed = processed.map((token, idx) =>
    acronymPreservedIndices.has(idx) ? token : applySuffixCorrection(token),
  );

  return processed.join(" ");
}
